import React, {useEffect, useState} from 'react';
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {
    DigitizationContentLeftPanel,
    DigitizationContentRightPanel,
    DigitizationContentSubtitle,
    StyledDigitizationCard,
    StyledDigitizationContent,
    StyledDigitizationEditionPage
} from "./DigitizationEdition.styled";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Typography
} from "@mui/material";
import DigitizationEditionToolbar from "./components/DigitizationEditionToolbar";
import {useDigitizationDetails} from "../../../hooks/useDigitizationDetails";
import FilePreviewOriginal from "../DigitizationDetails/components/FilePreviewOriginal";
import OcrResultEditor from "./components/OcrResultEditor";
import {
    LOCAL_DOCUMENTS_UNASSIGNABLE_TYPE,
    LocalDocumentCategory,
    OcrVersionPagesWithText
} from "../../../models/local-documents.models";
import {useSnackbar} from "../../../contexts/SnackbarContext";

const DigitizationEdition = () => {
    const {id} = useParams<{ id: string }>();
    const documentId = id ? parseInt(id) : undefined;

    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();

    const {
        contentData,
        originalBaseContent,
        ocrResult,
        isLoading,
        error,
        confirmOcrVersion,
        navigateBackToDetails,
        changeDocumentCategory,
    } = useDigitizationDetails(documentId);

    const [editedPages, setEditedPages] = useState<{pageNumber: number; text: string; confidencePercent: number}[]>([]);
    const [isDirty, setIsDirty] = useState<boolean>(false);

    const originalDocumentCategory = contentData?.type as LocalDocumentCategory ?? LOCAL_DOCUMENTS_UNASSIGNABLE_TYPE;

    const [isInitialized, setIsInitialized] = useState(false);

    const [category, setCategory] = useState<LocalDocumentCategory>(LOCAL_DOCUMENTS_UNASSIGNABLE_TYPE);
    const [isModified, setIsModified] = useState<boolean>(false);

    const [openCancelDialog, setOpenCancelDialog] = useState(false);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    useEffect(() => {
        setIsInitialized(false);
    }, [documentId]);

    useEffect(() => {
        if (!isInitialized && contentData) {
            setCategory(contentData.type as LocalDocumentCategory ?? LOCAL_DOCUMENTS_UNASSIGNABLE_TYPE);
            setIsInitialized(true);
        }
    }, [isInitialized, contentData]);

    useEffect(() => {
        setEditedPages(ocrResult?.pages?.map(({ pageNumber, text, confidencePercent }) =>
            ({ pageNumber, text, confidencePercent })) ?? []
        );
    }, [ocrResult]);

    useEffect(() => {
        if (error) {
            showSnackbar(t('digitizationEdition.messages.fetchDataError'), 'error');
        }
    }, [error, showSnackbar, t]);

    const handleConfirmDocument = async () => {
        if (isDirty) {
            const requestBody: OcrVersionPagesWithText = {
                pages: editedPages.map(({ pageNumber, text, confidencePercent }) => ({ pageNumber, text, confidencePercent })),
            };

            await confirmOcrVersion(requestBody);
            setIsDirty(false);
        }

        if (isModified) {
            if (category !== LOCAL_DOCUMENTS_UNASSIGNABLE_TYPE && category !== originalDocumentCategory) {
                await changeDocumentCategory(category);
            }

            setIsModified(false);
        }
        setOpenConfirmDialog(false);
    }

    const handleEditorChange = (page: number, value: string) => {
        const updated = editedPages.map(p => p.pageNumber === page ? { ...p, text: value } : p);

        setEditedPages(updated);
        setIsDirty(updated.some((p,i) => p.text !== ocrResult?.pages[i].text));
    };

    const handleCancelChanges = () => {
        setEditedPages(ocrResult?.pages?.map(({ pageNumber, text, confidencePercent }) =>
            ({ pageNumber, text, confidencePercent })) ?? []
        );
        setIsDirty(false);
        
        setCategory(originalDocumentCategory);
        setIsModified(false);

        setOpenCancelDialog(false);
    };

    const handleCategoryChange = (value: LocalDocumentCategory) => {
        setCategory(value);

        if (value !== originalDocumentCategory) {
            setIsModified(true);
        }
    };

    return (
        <StyledDigitizationEditionPage>
            <StyledDigitizationCard>
                {isLoading && (
                    <Stack justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress />
                    </Stack>
                )}

                {!isLoading && contentData && (
                    <DigitizationEditionToolbar
                        documentCategory={category}
                        isModified={isDirty || isModified}
                        onDocumentCategoryChange={handleCategoryChange}
                        onConfirmDocument={() => setOpenConfirmDialog(true)}
                        onCancelDocument={() => setOpenCancelDialog(true)}
                        navigateBack={navigateBackToDetails}
                    />
                )}

                {(!isLoading && (!contentData)) && (
                    <Stack justifyContent="center" alignItems="center" height="100%">
                        <Typography variant="h6">
                            {t('digitizationEdition.messages.noResultsInfo')}
                        </Typography>
                    </Stack>
                )}

                {(!isLoading && contentData) && (
                    <StyledDigitizationContent>
                        <DigitizationContentLeftPanel>
                            <DigitizationContentSubtitle variant="subtitle1">
                                {t('digitizationEdition.editor.originalSubtitle')}
                            </DigitizationContentSubtitle>

                            {(!originalBaseContent)
                                ? (<Typography variant="body1" fontWeight={500} color={'black'}>
                                        {t('digitizationEdition.messages.noOriginalDataFoundError')}
                                    </Typography>)
                                : (<FilePreviewOriginal
                                        fileName={contentData.fileName}
                                        contentType={contentData.contentType}
                                        baseContent={originalBaseContent}
                                    />)
                            }

                        </DigitizationContentLeftPanel>

                        <DigitizationContentRightPanel>
                            <DigitizationContentSubtitle variant="subtitle1">
                                {t('digitizationEdition.editor.ocrResultSubtitle')}
                            </DigitizationContentSubtitle>

                            {!ocrResult
                                ? (<Typography variant="body1" fontWeight={500} color={'black'}>
                                        {t('digitizationEdition.messages.noResultDataFoundError')}
                                    </Typography>)
                                : (<OcrResultEditor
                                    pages={ocrResult.pages}
                                    editedPages={editedPages}
                                    onChange={handleEditorChange}
                                />)
                            }
                        </DigitizationContentRightPanel>
                    </StyledDigitizationContent>
                )}
            </StyledDigitizationCard>

            {openCancelDialog && (
                <AcceptChangesDialog
                    isCancelled={true}
                    open={openCancelDialog}
                    onConfirm={handleCancelChanges}
                    onCancel={() => setOpenCancelDialog(false)}
                />
            )}

            {openConfirmDialog && (
                <AcceptChangesDialog
                    isCancelled={false}
                    open={openConfirmDialog}
                    onConfirm={handleConfirmDocument}
                    onCancel={() => setOpenConfirmDialog(false)}
                />
            )}
        </StyledDigitizationEditionPage>
    );
};


interface AcceptChangesDialogProps {
    isCancelled: boolean;
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

const AcceptChangesDialog = ({ isCancelled, open, onConfirm, onCancel }: AcceptChangesDialogProps) => {
    const { t } = useTranslation();
    const title = isCancelled ? t('digitizationEdition.dialog.cancelTitle') : t('digitizationEdition.dialog.confirmTitle');
    const message = isCancelled ? t('digitizationEdition.dialog.cancelMessage') : t('digitizationEdition.dialog.confirmMessage');
    const buttonLabel = isCancelled ? t('digitizationEdition.dialog.cancelBtn') : t('digitizationEdition.dialog.confirmBtn');

    return (
        <Dialog open={open} onClose={onCancel}>
            <DialogTitle>{title}</DialogTitle>

            <DialogContent>
                <DialogContentText>
                    {message}
                </DialogContentText>
            </DialogContent>

            <DialogActions>
                <Button onClick={onCancel} variant="outlined">
                    {t('digitizationEdition.dialog.back')}
                </Button>

                <Button onClick={onConfirm} variant="contained">
                    {buttonLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DigitizationEdition;